import {type Json, getObject, getString} from '@packages/utilities/json';
import {type Pair} from '@packages/utilities/Pair';

export type Codec<from, to> = {
  encode: (arg1: from) => to;
  decode: (arg1: to) => undefined | from;
};

export type JsonRpcRequest = {
  jsonrpc: '2.0';
  method: string;
  params: Json;
  id: string;
  meta?: Json;
};

export type JsonRpcResponse = {
  jsonrpc: '2.0';
  result: Json;
  id: string;
};

export type RpcConfig<req, res> = {
  name: string;
  req: Codec<req, Json>;
  res: Codec<res, Json>;
};

export type RpcAction<req, res> =
  | {
      tag: 'Method';
      val: RpcConfig<req, res>;
    }
  | {
      tag: 'Subscription';
      val: RpcConfig<req, res>;
    }
  | {
      tag: 'Unsubscription';
      val: RpcConfig<req, res>;
    };

type Response<state, res> = (finalState: state) => res;

type ResponderResult<state, action, res> =
  | {
      tag: 'Now';
      val: Response<state, res>;
    }
  | {
      tag: 'Later';
      val: (
        currentState: state,
        currentAction: action
      ) => Pair<null | Response<state, res>, state>;
    };

export const now = <state, action, res>(
  response: Response<state, res>
): ResponderResult<state, action, res> => ({tag: 'Now', val: response});

export const later = <state, action, res>(
  handler: (
    currentState: state,
    currentAction: action
  ) => Pair<null | Response<state, res>, state>
): ResponderResult<state, action, res> => ({tag: 'Later', val: handler});

export type ResponderHandler<state, action, req, res, context> = (
  initialState: state,
  params: req,
  context: context
) => Pair<ResponderResult<state, action, res>, state>;

export type RpcResponder<state, action, req, res, context> = {
  action: RpcAction<req, res>;
  handler: ResponderHandler<state, action, req, res, context>;
};

export function createRequest<req, res>(
  id: string,
  action: RpcAction<req, res>,
  params: req,
  meta?: Json
): JsonRpcRequest {
  return {
    jsonrpc: '2.0',
    id,
    method: action.val.name,
    params: action.val.req.encode(params),
    ...(meta !== undefined ? {meta} : {}),
  };
}

export function requestToJson(request: JsonRpcRequest): Json {
  return request;
}

export function createResponse<req, res>(
  id: string,
  action: RpcAction<req, res>,
  result: res
): JsonRpcResponse {
  return {
    jsonrpc: '2.0',
    id,
    result: action.val.res.encode(result),
  };
}

const createRpcUnsubscription = <req, res>(
  val: RpcConfig<req, res>
): RpcAction<req, res> => ({tag: 'Unsubscription', val});

// Trusted converts the JSON to the provided type. It is used when the value is trusted
// by either being created by us or it has been validated and we don't want to create a
// new object
export const trusted: <T, B>(value: B) => T = (value) => value as never;

export const unsubscribe: RpcAction<
  {
    id: string;
  },
  {
    id: string;
  }
> = createRpcUnsubscription<
  {
    id: string;
  },
  {
    id: string;
  }
>({
  name: 'unsubscribe',
  req: {
    encode: trusted,
    decode: (opts) => {
      const obj = getObject(opts);
      if (!obj) return;
      // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
      const id = getString(obj.id);
      if (!id) return;
      return trusted(obj);
    },
  },
  res: {
    encode: trusted,
    decode: trusted,
  },
});
